// Hand-written base classes for DSL-generated entity services.
//
// Each generated `src/services/{module}/{Entity}Service.ts` instantiates one of these
// with its baseUrl + entity name. The method bodies live here ONCE instead of being
// inlined into every generated file.
//
// Behaviour is identical to the previously-inlined generated code — do NOT change URLs,
// error semantics, or console.error text without also updating the generator
// (`tools/cli/src/generators/react/dexie-service.ts`) and the characterization test
// (`src/services/__tests__/generatedService.characterization.test.ts`).

import { apiClient, getErrorMessage } from '@/lib/apiClient';
import type {
  AbpPagedRequest,
  AbpPagedResponse,
  AbpGroupedResponse,
} from '@/lib/gridDataSourceUtils';

/**
 * Read-only entity service: list / grouped-list / aggregates / by-id.
 *
 * Used directly by read-only entities; extended by {@link EntityService} for full CRUD.
 * Read-only entities are typed as this base so they cannot call mutators at compile time.
 *
 * @typeParam T - the read model returned by the API.
 */
export class ReadOnlyEntityService<T> {
  /**
   * @param baseUrl    API route, e.g. `/api/inventory/inventoryonhand`.
   * @param entityName Display name used in `console.error` messages, preserving the
   *                   per-entity log text the inlined code produced.
   */
  constructor(
    protected readonly baseUrl: string,
    protected readonly entityName: string,
  ) {}

  /**
   * Get a paged, sorted, filtered list. Used by the DataGrid server-side data source.
   */
  async getList(params: AbpPagedRequest): Promise<AbpPagedResponse<T>> {
    try {
      const response = await apiClient.get<AbpPagedResponse<T>>(`${this.baseUrl}`, {
        params,
      });
      return response.data;
    } catch (error) {
      console.error(`Error fetching ${this.entityName} list:`, error);
      throw new Error(getErrorMessage(error));
    }
  }

  /**
   * Get grouped row counts for server-side DataGrid row grouping.
   */
  async getGroupedList(params: AbpPagedRequest): Promise<AbpGroupedResponse> {
    try {
      const response = await apiClient.get<AbpGroupedResponse>(`${this.baseUrl}/grouped-list`, {
        params,
      });
      return response.data;
    } catch (error) {
      console.error(`Error fetching ${this.entityName} grouped list:`, error);
      throw new Error(getErrorMessage(error));
    }
  }

  /**
   * Get server-side aggregations (sum, avg, min, max) for numeric fields.
   */
  async getAggregates(params: AbpPagedRequest): Promise<Record<string, number | null>> {
    try {
      const response = await apiClient.get<{ values: Record<string, number | null> }>(
        `${this.baseUrl}/aggregates`,
        { params },
      );
      return response.data.values;
    } catch (error) {
      console.error(`Error fetching ${this.entityName} aggregates:`, error);
      return {};
    }
  }

  /**
   * Get a single entity by ID.
   */
  async getById(id: string): Promise<T | undefined> {
    try {
      const response = await apiClient.get<T>(`${this.baseUrl}/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching ${this.entityName} ${id}:`, error);
      throw new Error(getErrorMessage(error));
    }
  }
}

/**
 * Full-CRUD entity service. Adds getAll / create / update / delete on top of the four
 * read methods.
 *
 * @typeParam T       - the read model.
 * @typeParam TCreate - the create payload (defaults to T).
 * @typeParam TUpdate - the update payload (defaults to T).
 */
export class EntityService<T, TCreate = T, TUpdate = T> extends ReadOnlyEntityService<T> {
  /**
   * Get all entities (legacy — use {@link getList} for paginated access).
   */
  async getAll(): Promise<T[]> {
    try {
      const response = await apiClient.get<{ items: T[] }>(`${this.baseUrl}`, {
        params: {
          maxResultCount: 1000,
        },
      });
      return response.data.items || [];
    } catch (error) {
      console.error(`Error fetching ${this.entityName}s:`, error);
      throw new Error(getErrorMessage(error));
    }
  }

  /**
   * Create a new entity.
   */
  async create(data: TCreate): Promise<T> {
    try {
      const response = await apiClient.post<T>(this.baseUrl, data);
      return response.data;
    } catch (error) {
      console.error(`Error creating ${this.entityName}:`, error);
      // Re-throw the original error to preserve Axios error structure for validation error extraction
      throw error;
    }
  }

  /**
   * Update an existing entity.
   */
  async update(id: string, data: TUpdate): Promise<T> {
    try {
      const response = await apiClient.put<T>(`${this.baseUrl}/${id}`, data);
      return response.data;
    } catch (error) {
      console.error(`Error updating ${this.entityName} ${id}:`, error);
      // Re-throw the original error to preserve Axios error structure for validation error extraction
      throw error;
    }
  }

  /**
   * Delete an entity.
   */
  async delete(id: string): Promise<void> {
    try {
      await apiClient.delete(`${this.baseUrl}/${id}`);
    } catch (error) {
      console.error(`Error deleting ${this.entityName} ${id}:`, error);
      throw new Error(getErrorMessage(error));
    }
  }

  /**
   * Delete multiple entities in one call (all-or-nothing on the server).
   */
  async deleteMany(ids: string[]): Promise<void> {
    try {
      await apiClient.post(`${this.baseUrl}/bulk-delete`, { ids });
    } catch (error) {
      console.error(`Error bulk deleting ${this.entityName}:`, error);
      // Re-throw the original error: the list view reads the refusal's code from the Axios error
      // structure, and the code is what links the message to its help section ("Read more").
      throw error;
    }
  }
}
